import { isNotNullish } from "@optee/utils";
import { z } from "zod";
import { UserType } from "./user.contant";

export type OperationGroupKey = "upcoming" | "in_progress" | "archived";

export enum OperationPipeline {
  PHASE_PROJET = "Phase projet",
  CHIFFRAGE_OFFRES = "Chiffrage et offres",
  LANCEMENT_OPERATION = "Lancement de l'opération",
  OPERATION_EN_COURS = "Opération en cours",
  FINALISATION_CEE = "Finalisation avec CEE",
  FACTURATION = "Facturation",
  ARCHIVAGE = "Archivage",
}

export const OperationPhaseLabelClientSchema = z.enum([
  "Préconisée",
  "Validation avant lancement",
  "Appel d'offres prévu",
  "Appel d'offres en cours",
  "⚠️ Projet bloqué",
  "Vérification éligibilité",
  "Production du devis",
  "⏳ En attente de signature",
  "⏳ Attente validation Coach Copro",
  "Devis validé",
  "⚠️ Opération annulée",
  "Facture en attente",
  "⏳ Paiement en attente",
  "Attente de lancement",
  "⚠️ Projet mis en pause",
  "Opération planifiée",
  "Opération en cours",
  "Opération en finalisation",
  "Opération terminée",
]);
export type OperationPhaseLabelClient = z.infer<
  typeof OperationPhaseLabelClientSchema
>;

export const OperationPhaseLabelProSchema = z.enum([
  "Appel d'offres à venir",
  "⚠️ Projet bloqué",
  "Vérification éligibilité",
  "En attente de devis / VT",
  "En attente de décision client",
  "Appel d'offres en cours",
  "Devis accepté",
  "⚠️ Opération annulée",
  "Opportunité perdue",
  "⏳ Facture requise",
  "Attente de lancement",
  "⚠️ Projet mis en pause",
  "Opération planifiée",
  "Opération en cours",
  "Opération en finalisation",
  "⏳ Documents CEE requis",
  "Vérification conformité",
  "Contrôle COFRAC",
  "En attente du délégataire",
  "Versement CEE",
  "⏳ Appel à facturation",
  "Attente paiement client",
  "Paiement en cours",
  "Génération facture commission",
  "⏳ Paiement requis",
  "Opération terminée",
]);
export type OperationPhaseLabelPro = z.infer<
  typeof OperationPhaseLabelProSchema
>;

export type PhaseInformation = {
  description: string;
  badge: "green" | "yellow" | "red";
  category: OperationGroupKey;
};

export type PhaseInformationPro = PhaseInformation & {
  label: OperationPhaseLabelPro;
};

export type PhaseInformationClient = PhaseInformation & {
  label: OperationPhaseLabelClient;
};

type OperationPhaseBase = {
  phase: string;
  enum: string;
  hsId: string;
};

export type VisibleOperationPhase = OperationPhaseBase & {
  visibleInApp: true;
  isLaunchable: boolean;
  pipeline: OperationPipeline;
  enum: string;
  hsId: string;
  clientInfos: PhaseInformationClient;
  proInfos?: PhaseInformationPro; // ProInfos is not defined until "Passation CSM" because it is not visible in the app since no quotes are linked yet
};

export type HiddenOperationPhase = OperationPhaseBase & {
  visibleInApp: false;
  isLaunchable: false;
  category?: undefined;
  pipeline?: undefined;
  clientInfos?: undefined;
  proInfos?: undefined;
};

export type OperationPhaseData = VisibleOperationPhase | HiddenOperationPhase;

/**
 * Defines all phases possible for an operation
 *
 * @see https://docs.google.com/spreadsheets/d/1-adeTUi2thR9qodIPQ0HA5fnlu5R-YG-kZpiisgCdAc/edit?pli=1&gid=1660150790#gid=1660150790
 */
export const OPERATION_PHASES_DATA: OperationPhaseData[] = [
  {
    visibleInApp: false,
    isLaunchable: false,
    phase: "UPSELL - Audit à récupérer",
    enum: "UPSELL",
    hsId: "1213338842",
  },
  {
    visibleInApp: false,
    isLaunchable: false,
    phase: "UPSELL - Audit récupéré",
    enum: "UPSELL_RECOVERED",
    hsId: "1890531575",
  },

  {
    visibleInApp: true,
    isLaunchable: true,
    pipeline: OperationPipeline.PHASE_PROJET,
    phase: "Phase projet",
    enum: "PROJECT_PHASE",
    hsId: "694365148",
    clientInfos: {
      category: "upcoming",
      label: "Préconisée",
      badge: "green",
      description:
        "Cette solution est recommandée pour améliorer la performance énergétique ou structurelle de votre bâtiment.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: true,
    pipeline: OperationPipeline.PHASE_PROJET,
    phase: "Intérêt confirmé",
    enum: "CONFIRMED_INTEREST",
    hsId: "694365149",
    clientInfos: {
      category: "upcoming",
      label: "Préconisée",
      badge: "green",
      description:
        "Cette solution est recommandée pour améliorer la performance énergétique ou structurelle de votre bâtiment.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.CHIFFRAGE_OFFRES,
    phase: "Pre lancement",
    enum: "PRE_LAUNCH",
    hsId: "2031421667",
    clientInfos: {
      category: "in_progress",
      label: "Validation avant lancement",
      badge: "yellow",
      description:
        "L’opération doit d’abord être validée par l’équipe Optee avant que les professionnels puissent soumettre leurs devis.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.CHIFFRAGE_OFFRES,
    phase: "Passation CSM",
    enum: "CSM_PASSATION",
    hsId: "703741912",
    clientInfos: {
      category: "in_progress",
      label: "Appel d'offres en cours",
      badge: "green",
      description:
        "Les entreprises sont en train de répondre à l’appel d’offres. Vous recevrez des demandes de visite technique ou des devis dans les prochains jours.",
    },
    proInfos: {
      category: "upcoming",
      label: "Appel d'offres à venir",
      badge: "green",
      description: "L’appel d’offres va bientôt être lancé par le client.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.CHIFFRAGE_OFFRES,
    phase: "À venir",
    enum: "TO_COME",
    hsId: "705801954",
    clientInfos: {
      category: "in_progress",
      badge: "green",
      label: "Appel d'offres prévu",
      description:
        "L'appel d'offres pour cette opération est planifié mais n’a pas encore été lancé. Vous pouvez encore ajuster le périmètre si besoin.",
    },
    proInfos: {
      category: "upcoming",
      label: "Appel d'offres à venir",
      badge: "green",
      description: "L’appel d’offres va bientôt être lancé par le client.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.CHIFFRAGE_OFFRES,
    phase: "À re-qualifier",
    enum: "TO_REQUALIFY",
    hsId: "761117135",
    clientInfos: {
      category: "in_progress",
      label: "⚠️ Projet bloqué",
      badge: "red",
      description:
        "Le processus est temporairement arrêté. Veuillez nous contacter pour débloquer la situation ou ajuster le projet.",
    },
    proInfos: {
      category: "upcoming",
      label: "⚠️ Projet bloqué",
      badge: "red",
      description: "L’appel d’offres est temporairement suspendu.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.CHIFFRAGE_OFFRES,
    phase: "Recherche Pro",
    enum: "PRO_SEARCH",
    hsId: "836338411",
    clientInfos: {
      category: "in_progress",
      label: "Appel d'offres en cours",
      badge: "green",
      description:
        "Nous collectons les offres des entreprises partenaires. Vous serez notifié dès qu’un devis sera disponible.",
    },
    proInfos: {
      category: "upcoming",
      label: "Appel d'offres en cours",
      badge: "green",
      description: "L’appel d’offres est en cours.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.CHIFFRAGE_OFFRES,
    phase: "À prendre en charge",
    enum: "TO_BE_TAKEN_IN_CHARGE",
    hsId: "919881970",
    clientInfos: {
      category: "in_progress",
      label: "Appel d'offres en cours",
      badge: "green",
      description:
        "Nous collectons les offres des entreprises partenaires. Vous serez notifié dès qu’un devis sera disponible.",
    },
    proInfos: {
      category: "upcoming",
      label: "Appel d'offres en cours",
      badge: "green",
      description: "L’appel d’offres est en cours.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.CHIFFRAGE_OFFRES,
    phase: "Vérification éligibilité - Délégataire",
    enum: "ELIGIBILITY_VERIFICATION",
    hsId: "1896500466",
    clientInfos: {
      category: "in_progress",
      label: "Vérification éligibilité",
      badge: "green",
      description: "Nous vérifions l'éligibilité de CEE de votre devis",
    },
    proInfos: {
      category: "upcoming",
      label: "Vérification éligibilité",
      badge: "green",
      description:
        "L’éligibilité du devis aux CEE est en cours de vérification.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.CHIFFRAGE_OFFRES,
    phase: "Génération d'une offre",
    enum: "OFFER_GENERATION",
    hsId: "appointmentscheduled",
    clientInfos: {
      category: "in_progress",
      label: "Production du devis",
      badge: "green",
      description:
        "Au moins une entreprise prépare actuellement son devis. Vous serez averti dès qu’il sera disponible pour consultation.",
    },
    proInfos: {
      category: "upcoming",
      label: "En attente de devis / VT",
      badge: "green",
      description: "L’appel d’offres est en cours.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.CHIFFRAGE_OFFRES,
    phase: "Négociation commerciale",
    enum: "COMMERCIAL_NEGOTIATION",
    hsId: "689981135",
    clientInfos: {
      category: "in_progress",
      label: "⏳ En attente de signature",
      badge: "yellow",
      description:
        "Le devis vous a été transmis. La validation est en attente pour pouvoir démarrer les travaux.",
    },
    proInfos: {
      category: "upcoming",
      label: "En attente de décision client",
      badge: "green",
      description:
        "Le client analyse les devis reçus et finalisera bientôt son choix.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.CHIFFRAGE_OFFRES,
    phase: "Attente Coach Copro",
    enum: "WAITING_FOR_COACH_COPRO",
    hsId: "701687530",
    clientInfos: {
      category: "in_progress",
      label: "⏳ Attente validation Coach Copro",
      badge: "yellow",
      description: "En attente de validation du devis",
    },
    proInfos: {
      category: "upcoming",
      label: "Appel d'offres en cours",
      badge: "green",
      description: "Contrôle du devis en cours.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.CHIFFRAGE_OFFRES,
    phase: "Devis signé",
    enum: "SIGNED_QUOTE",
    hsId: "675694020",
    clientInfos: {
      category: "in_progress",
      label: "Devis validé",
      badge: "green",
      description:
        "Vous avez validé le devis. Nous allons organiser le lancement de l’opération avec l’entreprise.",
    },
    proInfos: {
      category: "in_progress",
      label: "Devis accepté",
      badge: "green",
      description:
        "Votre devis a été accepté, préparation du lancement en cours.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    phase: "Fermé perdu",
    pipeline: OperationPipeline.ARCHIVAGE,
    enum: "CLOSED_LOST_BYPASS_PIPELINE",
    hsId: "1467121872",
    clientInfos: {
      category: "archived",
      label: "⚠️ Opération annulée",
      badge: "red",
      description:
        "L’opération a été annulée. Aucune suite ne sera engagée pour ce projet.",
    },
    proInfos: {
      category: "archived",
      label: "⚠️ Opération annulée",
      badge: "red",
      description:
        "L’opération a été annulée. Aucune suite ne sera engagée pour ce projet.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.LANCEMENT_OPERATION,
    phase: "Récupération facture du Pro",
    enum: "LAUNCHING_PRO_INVOICE_RECOVERY",
    hsId: "693123273",
    clientInfos: {
      category: "in_progress",
      label: "Facture en attente",
      badge: "green",
      description: "En attente du prestataire",
    },
    proInfos: {
      category: "in_progress",
      label: "⏳ Facture requise",
      badge: "yellow",
      description: "Merci d’envoyer votre facture pour avancer sur le projet.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.LANCEMENT_OPERATION,
    phase: "Envoi appel de provisions",
    enum: "SEND_PROVISION_CALL",
    hsId: "693123274",
    clientInfos: {
      category: "in_progress",
      label: "⏳ Paiement en attente",
      badge: "yellow",
      description:
        "Nous attendons la facture de l’entreprise pour pouvoir avancer sur l’opération.",
    },
    proInfos: {
      category: "in_progress",
      label: "Attente paiement client",
      badge: "green",
      description: "En attente du paiement du client.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.LANCEMENT_OPERATION,
    phase: "En attente du paiement client",
    enum: "LAUNCHING_WAITING_FOR_CLIENT_PAYMENT",
    hsId: "693123275",
    clientInfos: {
      category: "in_progress",
      label: "⏳ Paiement en attente",
      badge: "yellow",
      description: "Nous attendons votre paiement pour poursuivre l’opération.",
    },
    proInfos: {
      category: "in_progress",
      label: "Attente paiement client",
      badge: "yellow",
      description: "En attente du paiement du client.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.LANCEMENT_OPERATION,
    phase: "Paiement du Pro",
    enum: "PRO_PAYMENT",
    hsId: "693123276",
    clientInfos: {
      category: "in_progress",
      label: "Attente de lancement",
      badge: "green",
      description:
        "Tout est prêt. Nous finalisons les derniers éléments avant le démarrage de l’opération.",
    },
    proInfos: {
      category: "in_progress",
      label: "Paiement en cours",
      badge: "green",
      description:
        "Le paiement de l'acompte du client est en cours de traitement.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.LANCEMENT_OPERATION,
    phase: "Lancement de l'opération",
    enum: "OPERATION_LAUNCH",
    hsId: "693123277",
    clientInfos: {
      category: "in_progress",
      label: "Attente de lancement",
      description:
        "Tout est prêt. Nous finalisons les derniers éléments avant le démarrage de l’opération.",
      badge: "green",
    },
    proInfos: {
      category: "in_progress",
      label: "Attente de lancement",
      badge: "green",
      description: "Tout est prêt. L’opération va bientôt démarrer.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.LANCEMENT_OPERATION,
    phase: "Standby",
    enum: "STANDBY",
    hsId: "703353294",
    clientInfos: {
      category: "in_progress",
      label: "⚠️ Projet mis en pause",
      description:
        "L’opération est momentanément suspendue. Contactez-nous si vous souhaitez la relancer.",
      badge: "red",
    },
    proInfos: {
      category: "in_progress",
      label: "⚠️ Projet mis en pause",
      description:
        "L’opération est momentanément suspendue. Contactez-nous pour plus de détails.",
      badge: "yellow",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.LANCEMENT_OPERATION,
    phase: "Lancement organisé",
    enum: "LAUNCH_ORGANIZED",
    hsId: "693123278",
    clientInfos: {
      category: "in_progress",
      label: "Opération planifiée",
      description:
        "Les dates d’intervention sont fixées. Le lancement est imminent.",
      badge: "green",
    },
    proInfos: {
      category: "in_progress",
      label: "Opération planifiée",
      description: "L’intervention est planifiée. Lancement imminent.",
      badge: "green",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.OPERATION_EN_COURS,
    phase: "Suivi de l'opération",
    enum: "OPERATION_FOLLOW_UP",
    hsId: "693324987",
    clientInfos: {
      category: "in_progress",
      label: "Opération en cours",
      badge: "green",
      description:
        "Les travaux sont en cours. Nous assurons le suivi avec l’entreprise.",
    },
    proInfos: {
      category: "in_progress",
      label: "Opération en cours",
      badge: "green",
      description: "Les travaux sont en cours.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.OPERATION_EN_COURS,
    phase: "Fin de travaux",
    enum: "WORK_COMPLETION",
    hsId: "698025953",
    clientInfos: {
      category: "in_progress",
      label: "Opération en finalisation",
      badge: "green",
      description:
        "Les derniers ajustements ou vérifications sont en cours pour clôturer l’opération.",
    },
    proInfos: {
      category: "in_progress",
      label: "Opération en finalisation",
      badge: "green",
      description: "Les travaux ont été déclarés terminés.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.FINALISATION_CEE,
    phase: "Pièces du dossier de conformité",
    enum: "CONFORMITY_FILE_ATTACHMENTS",
    hsId: "693324995",
    clientInfos: {
      category: "in_progress",
      label: "Opération en finalisation",
      badge: "green",
      description:
        "Les derniers ajustements ou vérifications sont en cours pour clôturer l’opération.",
    },
    proInfos: {
      category: "in_progress",
      label: "⏳ Documents CEE requis",
      badge: "yellow",
      description: "Documents CEE à transmettre.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.FINALISATION_CEE,
    phase: "Vérification dossier conformité",
    enum: "CONFORMITY_FILE_VERIFICATION",
    hsId: "1500858581",
    clientInfos: {
      category: "in_progress",
      label: "Opération en finalisation",
      badge: "green",
      description:
        "Les derniers ajustements ou vérifications sont en cours pour clôturer l’opération.",
    },
    proInfos: {
      category: "in_progress",
      label: "Vérification conformité",
      badge: "green",
      description: "Contrôle en cours des documents fournis.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.FINALISATION_CEE,
    phase: "Dépôt dossier au délégataire",
    enum: "FILE_REVIEW_BY_DELEGATE",
    hsId: "693570530",
    clientInfos: {
      category: "in_progress",
      label: "Opération en finalisation",
      description:
        "Les derniers ajustements ou vérifications sont en cours pour clôturer l’opération.",
      badge: "green",
    },
    proInfos: {
      category: "in_progress",
      label: "Vérification conformité",
      badge: "green",
      description: "Le dossier a été transmis au délégataire pour validation.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.FINALISATION_CEE,
    phase: "COFRAC en cours",
    enum: "COFRAC_IN_PROGRESS",
    hsId: "1663340755",
    clientInfos: {
      category: "in_progress",
      label: "Opération en finalisation",
      badge: "green",
      description:
        "Les derniers ajustements ou vérifications sont en cours pour clôturer l’opération.",
    },
    proInfos: {
      category: "in_progress",
      label: "Contrôle COFRAC",
      badge: "green",
      description: "Le dossier a été transmis au délégataire pour validation.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.FINALISATION_CEE,
    phase: "Facturation au délégataire",
    enum: "INVOICING_TO_DELEGATE",
    hsId: "693570531",
    clientInfos: {
      category: "in_progress",
      label: "Opération en finalisation",
      badge: "green",
      description:
        "Les derniers ajustements ou vérifications sont en cours pour clôturer l’opération.",
    },
    proInfos: {
      category: "in_progress",
      label: "En attente du délégataire",
      badge: "green",
      description:
        "En attente de validation et de financement par le délégataire CEE.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.FINALISATION_CEE,
    phase: "Réception du financement",
    enum: "FUNDING_RECEIPT",
    hsId: "693570532",
    clientInfos: {
      category: "in_progress",
      label: "Opération en finalisation",
      badge: "green",
      description:
        "Les derniers ajustements ou vérifications sont en cours pour clôturer l’opération.",
    },
    proInfos: {
      category: "in_progress",
      label: "En attente du délégataire",
      badge: "green",
      description:
        "En attente de validation et de financement par le délégataire CEE.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.FINALISATION_CEE,
    phase: "Versement CEE",
    enum: "PAYMENT_CEE",
    hsId: "1932423400",
    clientInfos: {
      category: "in_progress",
      label: "Opération en finalisation",
      badge: "green",
      description:
        "Les derniers ajustements ou vérifications sont en cours pour clôturer l’opération.",
    },
    proInfos: {
      category: "in_progress",
      label: "Versement CEE",
      badge: "green",
      description:
        "En attente de validation et de financement par le délégataire CEE.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.FACTURATION,
    phase: "Fin de travaux - Appel à facturation",
    enum: "WORK_COMPLETION_DOCUMENTS_RECOVERY",
    hsId: "693375986",
    clientInfos: {
      category: "in_progress",
      label: "Opération en finalisation",
      badge: "green",
      description:
        "Les derniers ajustements ou vérifications sont en cours pour clôturer l’opération.",
    },
    proInfos: {
      category: "in_progress",
      label: "⏳ Appel à facturation",
      badge: "yellow",
      description: "En attente de votre facture de fin de travaux.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.FACTURATION,
    phase: "Envoi de l'appel de provisions au client",
    enum: "SEND_PROVISION_CALL_TO_CLIENT",
    hsId: "693375987",
    clientInfos: {
      category: "in_progress",
      label: "Opération en finalisation",
      badge: "green",
      description:
        "Les derniers ajustements ou vérifications sont en cours pour clôturer l’opération.",
    },
    proInfos: {
      category: "in_progress",
      label: "Attente paiement client",
      badge: "yellow",
      description: "Votre facture a été transmise au client.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.FACTURATION,
    phase: "En attente du paiement client",
    enum: "WAITING_FOR_CLIENT_PAYMENT",
    hsId: "693375988",
    clientInfos: {
      category: "in_progress",
      label: "⏳ Paiement en attente",
      badge: "yellow",
      description:
        "Nous attendons la réception du paiement pour pouvoir poursuivre l’opération.",
    },
    proInfos: {
      category: "in_progress",
      label: "Attente paiement client",
      badge: "green",
      description: "En attente du paiement final du client.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.FACTURATION,
    phase: "Paiement vers fournisseur",
    enum: "SUPPLIER_PAYMENT_POST_CEE",
    hsId: "1500729560",
    clientInfos: {
      category: "archived",
      label: "Opération terminée",
      badge: "green",
      description:
        "L’opération est achevée. Vous pouvez consulter les documents de fin de mission dans l’interface.",
    },
    proInfos: {
      category: "in_progress",
      label: "Paiement en cours",
      badge: "green",
      description: "Le paiement final est en cours de traitement.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.FACTURATION,
    phase: "Envoi facture de commission",
    enum: "COMMISSION_TRANSACTION",
    hsId: "693375989",
    clientInfos: {
      category: "archived",
      label: "Opération terminée",
      badge: "green",
      description:
        "L’opération est achevée. Vous pouvez consulter les documents de fin de mission dans l’interface.",
    },
    proInfos: {
      category: "in_progress",
      label: "Génération facture commission",
      badge: "green",
      description: "La facture de commission Optee a été générée.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.FACTURATION,
    phase: "En attente du paiement de la commission",
    enum: "AWAITING_COMMISSION_TRANSACTION",
    hsId: "1306715322",
    clientInfos: {
      category: "archived",
      label: "Opération terminée",
      badge: "green",
      description:
        "L’opération est achevée. Vous pouvez consulter les documents de fin de mission dans l’interface.",
    },
    proInfos: {
      category: "in_progress",
      label: "⏳ Paiement requis",
      badge: "yellow",
      description: "En attente du règlement de la commission Optee.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.FACTURATION,
    phase: "Archivage",
    enum: "FINALISATION_ARCHIVING",
    hsId: "697959357",
    clientInfos: {
      category: "archived",
      label: "Opération terminée",
      badge: "green",
      description:
        "L’opération est achevée. Vous pouvez consulter les documents de fin de mission dans l’interface.",
    },
    proInfos: {
      category: "archived",
      label: "Opération terminée",
      badge: "green",
      description: "L’opération est terminée. Aucune action n’est requise.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.ARCHIVAGE,
    phase: "Opération archivée",
    enum: "ARCHIVED_OPERATION",
    hsId: "697959364",
    clientInfos: {
      category: "archived",
      label: "Opération terminée",
      badge: "green",
      description:
        "L’opération est achevée. Vous pouvez consulter les documents de fin de mission dans l’interface.",
    },
    proInfos: {
      category: "archived",
      label: "Opération terminée",
      badge: "green",
      description: "L’opération est terminée. Aucune action n’est requise.",
    },
  },
  {
    visibleInApp: true,
    isLaunchable: false,
    pipeline: OperationPipeline.ARCHIVAGE,
    phase: "Fermé perdu",
    enum: "CLOSED_LOST",
    hsId: "705701620",
    clientInfos: {
      category: "archived",
      label: "⚠️ Opération annulée",
      badge: "red",
      description:
        "L’opération a été annulée. Aucune suite ne sera engagée pour ce projet.",
    },
    proInfos: {
      category: "archived",
      label: "⚠️ Opération annulée",
      badge: "red",
      description:
        "L’opération a été annulée. Aucune suite ne sera engagée pour ce projet.",
    },
  },
];

export type OperationPhase = (typeof OPERATION_PHASES_DATA)[number];
export type OperationPhaseEnum = OperationPhase["enum"];
export type OperationPhaseId = OperationPhase["hsId"];

export const OPERATION_PHASES_ENUMS = OPERATION_PHASES_DATA.map(
  (phase) => phase.enum,
) as [OperationPhaseEnum, ...OperationPhaseEnum[]];

export const OPERATION_PHASES_IDS = OPERATION_PHASES_DATA.map(
  (phase) => phase.hsId,
) as [OperationPhaseId, ...OperationPhaseId[]];

export const VISIBLE_OPERATION_PHASES = OPERATION_PHASES_DATA.filter(
  (phase) => phase.visibleInApp,
);

export const OPERATION_PHASES_LABELS_CLIENT: OperationPhaseLabelClient[] =
  Array.from(
    new Set(VISIBLE_OPERATION_PHASES.map((phase) => phase.clientInfos.label)),
  );

export const OPERATION_PHASES_LABELS_PRO: OperationPhaseLabelPro[] = Array.from(
  new Set(
    VISIBLE_OPERATION_PHASES.map((phase) => phase.proInfos?.label).filter(
      isNotNullish,
    ),
  ),
);

export const OPERATION_PHASES_IDS_ALLOWED_IN_APP = VISIBLE_OPERATION_PHASES.map(
  (phase) => phase.hsId,
);

export const OPERATION_PHASES_IN_PROGRESS = VISIBLE_OPERATION_PHASES.filter(
  (phase) => phase.clientInfos.category === "in_progress",
).map((phase) => phase.hsId) as OperationPhaseId[];

export const getOperationPhaseFromEnum = (phaseEnum: OperationPhaseEnum) => {
  const phaseData = OPERATION_PHASES_DATA.find((p) => p.enum === phaseEnum);

  if (!phaseData) {
    throw new Error(`No phase found for ${phaseEnum}`);
  }

  return phaseData;
};

export type OperationPhaseInfos = Omit<
  OperationPhase,
  "clientInfos" | "proInfos"
> &
  (PhaseInformationPro | PhaseInformationClient);

export const getOperationPhaseFromHsId = (
  phaseHsId: OperationPhaseId,
  userType: UserType = UserType.CLIENT,
): OperationPhaseInfos => {
  const phaseData = OPERATION_PHASES_DATA.find((p) => p.hsId === phaseHsId);

  if (!phaseData) {
    throw new Error(`No phase found for ${phaseHsId}`);
  }

  if (userType === UserType.CLIENT) {
    if (!phaseData.clientInfos) {
      throw new Error(`No client infos found for ${phaseHsId}`);
    }
    return {
      ...phaseData,
      ...phaseData.clientInfos,
    };
  }
  if (!phaseData.proInfos) {
    throw new Error(`No pro infos found for ${phaseHsId}`);
  }

  return {
    ...phaseData,
    ...phaseData.proInfos,
  };
};

export const getOperationPhaseFromHsIdForAdmin = (
  phaseHsId: OperationPhaseId,
) => {
  const phaseData = OPERATION_PHASES_DATA.find((p) => p.hsId === phaseHsId);

  if (!phaseData) {
    throw new Error(`No phase found for ${phaseHsId}`);
  }
  return phaseData;
};
